import './styles.scoped.css'
import {useState, useEffect, useRef} from 'react'
import {
    Typography,
    Button,
    TextField,
    Select,
    InputAdornment,
    MenuItem,
    IconButton,
    Chip,
    Menu,
    FormControl,
    Checkbox,
    ListItemText,
    Skeleton,
    Avatar,
    CircularProgress,
    Box,
} from '@mui/material'
import {Search, MoreVert, Menu as MenuIcon, FilterAlt, PersonAddAlt1} from '@mui/icons-material'
import {DataGrid} from '@mui/x-data-grid'
import {DataGridFooter, NotificationIcon, SkeletonContainer, TableSkeleton, TextSkeleton, AvatarSkeleton, ChipSkeleton} from '../../components'
import {caregiverApi, clientApi} from '../../services'
import {format} from 'date-fns'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {useNavigate} from 'react-router-dom'
import {useMobile} from '../../hooks'
import {getInitials, getColorFromName, parseError} from '../../utils/helpers'
import InfiniteScroll from 'react-infinite-scroll-component'
import {MobileFilter, DeactivateClientDialog, ReactivateClientDialog} from './components'
import {noResultsFound} from '../../assets'

export default () => {
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [rows, setRows] = useState([])
    const [rowCount, setRowCount] = useState(0)
    const [sortBy, setSortBy] = useState(null)
    const [sortOrder, setSortOrder] = useState(null)
    const [search, setSearch] = useState(null)
    const [searchTyping, setSearchTyping] = useState(null)
    const {setLoading, setMessageBox, setMobileSidebar, branchId} = useGlobalStates()
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedRow, setSelectedRow] = useState(null)
    const [openMobileFilter, setOpenMobileFilter] = useState(false)
    const [hasMoreScroll, setHasMoreScroll] = useState(true)
    const [scrollStep, setScrollStep] = useState(1)
    const [scrollData, setScrollData] = useState([])
    const [openDeactivateClientDialog, setOpenDeactivateClientDialog] = useState(false)
    const [openReactivateClientDialog, setOpenReactivateClientDialog] = useState(false)
    const navigate = useNavigate()
    const statuses = [
        {id: 'active', display_name: 'Active'},
        {id: 'deactivated', display_name: 'Deactivated'},
        {id: 'pending', display_name: 'Pending Invitation'},
    ]
    const [selectedStatuses, setSelectedStatuses] = useState(statuses.map(r => r.id))
    const [services, setServices] = useState([])
    const [selectedServices, setSelectedServices] = useState([])
    const [searchServiceTerm, setSearchServiceTerm] = useState('')
    const filteredServices = services?.filter(item => item?.display_name?.toLowerCase().includes(searchServiceTerm?.toLowerCase()))
    const [skeleton, setSkeleton] = useState(true)
    const requestId = useRef(0)
    const scrollRequestId = useRef(0)
    const {mobile} = useMobile()
    const menuRef = useRef(null)

    useEffect(() => {
        if (mobile) {
            setMobileSidebar(false)
        }
    }, [])

    useEffect(() => {
        if (branchId) {
            clientApi
                .getBranchServices({branch_id: branchId})
                .then(({data: {data}}) => {
                    setServices(data?.map(item => ({...item, id: item?.service_id, display_name: item?.service_name})))
                    setSelectedServices(data?.map(item => item?.service_id))
                })
                .catch(error => {
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                })
        }
    }, [branchId])

    useEffect(() => {
        const timeout = setTimeout(() => {
            loadData()
        }, 1000)
        return () => clearTimeout(timeout)
    }, [branchId, page, pageSize, sortBy, sortOrder, search, selectedStatuses, selectedServices, mobile])

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (mobile && scrollStep == 1) {
                loadScrollData()
            }
        }, 1000)
        return () => clearTimeout(timeout)
    }, [branchId, scrollStep, mobile])

    useEffect(() => {
        const timeout = setTimeout(() => {
            setSearch(searchTyping)
        }, 1000)
        return () => clearTimeout(timeout)
    }, [searchTyping])

    // silent: a refetch after a mutation keeps the rows on screen behind the global spinner instead of flashing the skeleton.
    const loadData = (silent = false) => {
        if (mobile) {
            if (scrollStep != 1) {
                scrollRequestId.current++
                setSkeleton(true)
            }
            setScrollData([])
            setScrollStep(1)
        } else {
            const id = ++requestId.current
            if (silent) {
                setLoading(true)
            } else {
                setSkeleton(true)
            }
            caregiverApi
                .getBranchCaregivers(branchId, {
                    page: page + 1,
                    page_size: pageSize,
                    search: search,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    status: selectedStatuses?.length === statuses?.length ? null : selectedStatuses?.join(','),
                    service_ids: selectedServices?.filter(Boolean)?.length === services?.length ? null : selectedServices?.filter(Boolean)?.join(','),
                })
                .then(({data}) => {
                    if (silent) setLoading(false)
                    if (id !== requestId.current) return
                    setRows(data?.caregivers)
                    setRowCount(data?.total)
                    setSkeleton(false)
                })
                .catch(error => {
                    if (silent) setLoading(false)
                    if (id !== requestId.current) return
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                    setSkeleton(false)
                })
        }
    }

    const loadScrollData = () => {
        const id = ++scrollRequestId.current
        if (branchId) {
            if (scrollStep == 1) {
                setSkeleton(true)
            }
            caregiverApi
                .getBranchCaregivers(branchId, {
                    page: scrollStep,
                    limit: 10,
                    search: search,
                    sort_by: sortBy,
                    sort_order: sortOrder,
                    status: selectedStatuses?.length === statuses?.length ? null : selectedStatuses?.join(','),
                    service_ids: selectedServices?.filter(Boolean)?.length === services?.length ? null : selectedServices?.filter(Boolean)?.join(','),
                })
                .then(({data}) => {
                    if (id !== scrollRequestId.current) return
                    if (data) {
                        setScrollData(prev => [...prev, ...data?.caregivers])
                    }
                    setHasMoreScroll(data?.caregivers?.length > 0)
                    setScrollStep(prev => ++prev)
                    if (scrollStep == 1) {
                        setSkeleton(false)
                    }
                })
                .catch(error => {
                    if (id !== scrollRequestId.current) return
                    const {title, description} = parseError(error)
                    if (description) {
                        setMessageBox({status: 'error', title, description})
                    }
                    if (scrollStep == 1) {
                        setSkeleton(false)
                    }
                })
        }
    }

    const statusChange = event => {
        const value = event.target.value
        if (value.includes('all')) {
            if (selectedStatuses.length === statuses.length) {
                setSelectedStatuses([])
            } else {
                setSelectedStatuses(statuses.map(r => r.id))
            }
        } else {
            setSelectedStatuses(value)
        }
    }

    const renderStatusLabel = selecteds => {
        if (selecteds.length === statuses.length) {
            return 'All Statuses'
        }
        return statuses
            .filter(r => selecteds.includes(r.id))
            .map(r => r.display_name)
            .join(', ')
    }

    const serviceChange = event => {
        const value = event.target.value
        if (value.includes('all')) {
            const allFilteredIds = filteredServices.map(c => c.id)
            const isAllSelected = allFilteredIds.every(id => selectedServices.includes(id))
            if (isAllSelected) {
                setSelectedServices(prev => prev.filter(id => !allFilteredIds.includes(id)))
            } else {
                setSelectedServices(prev => [...new Set([...prev, ...allFilteredIds])])
            }
        } else {
            setSelectedServices(value)
        }
    }

    const renderServiceLabel = (selecteds = []) => {
        const cleanValues = selecteds.filter(Boolean)
        if (services.length > 0 && cleanValues.length === services.length) {
            return 'All Services'
        }
        return services
            .filter(r => cleanValues.includes(r.id))
            .map(r => r.display_name)
            .join(', ')
    }

    const sortChange = data => {
        if (data.length > 0) {
            const {field, sort} = data[0]
            setSortBy(field)
            setSortOrder(sort)
        } else {
            setSortBy(null)
            setSortOrder(null)
        }
    }

    const menuOpen = (e, row) => {
        setAnchorEl(e.currentTarget)
        setSelectedRow(row)
    }

    const menuClose = () => {
        setAnchorEl(null)
    }

    const editCaregiverDetails = () => {
        menuClose()
        navigate(`/caregivers-management/${selectedRow?.id}/personal-information`)
    }

    const removeCaregiver = () => {
        menuClose()
        setLoading(true)
        caregiverApi
            .deleteCaregiver(branchId, selectedRow?.id)
            .then(() => {
                setLoading(false)
                loadData(true)
                setMessageBox({
                    status: 'success',
                    title: 'Caregiver Removed Successfully',
                    description: 'Caregiver have been successfully removed. All changes are now reflected in the system.',
                })
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    const deactivateCaregiver = () => {
        setOpenDeactivateClientDialog(true)
        menuClose()
    }

    const reactivateCaregiver = () => {
        setOpenReactivateClientDialog(true)
        menuClose()
    }

    const mobileReset = () => {
        setSelectedStatuses([])
        setSortBy('')
        setSortOrder('')
        setScrollData([])
        setScrollStep(1)
    }

    const onRowClick = row => {
        navigate(`/caregivers-management/${row?.id}/personal-information`)
    }

    const columns = [
        {
            field: 'caregiver',
            headerName: 'Caregiver',
            flex: 1.6,
            valueGetter: (value, row) => {
                return row?.first_name + ' ' + row?.last_name
            },
            renderCell: ({row}) => (
                <div className="people-card">
                    {row?.avatar_url ? (
                        <Avatar src={row?.avatar_url} style={{width: 32, height: 32}} />
                    ) : (
                        <Avatar
                            style={{
                                backgroundColor: getColorFromName(row?.first_name + ' ' + row?.last_name),
                                width: 32,
                                height: 32,
                                fontSize: 16,
                            }}>
                            {getInitials(row?.first_name + ' ' + row?.last_name)}
                        </Avatar>
                    )}
                    <Typography variant="body2" color="text.primary" className="name">
                        {row?.first_name + ' ' + row?.last_name}
                    </Typography>
                </div>
            ),
        },
        {field: 'email', headerName: 'Email', flex: 1},
        {
            field: 'primary_phone',
            headerName: 'Phone Number',
            flex: 1,
            valueGetter: (value, row) => {
                return row?.contacts?.[0]?.phones?.find(phone => phone.is_primary)?.number
            },
        },
        {
            field: 'services',
            headerName: 'Services',
            flex: 1.2,
            renderCell: ({row}) => (
                <div className="services">
                    {row?.services && row?.services?.length > 1 ? (
                        <Typography variant="body2" color="text.primary">
                            {`${(row?.services?.[0]?.service_name ?? '').length > 20 ? (row?.services?.[0]?.service_name ?? '').substring(0, 20) + '...' : (row?.services?.[0]?.service_name ?? '')}, `}{' '}
                            <b>+{row?.services?.length - 1}</b>
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.primary">
                            {`${(row?.services?.[0]?.service_name ?? '').length > 20 ? (row?.services?.[0]?.service_name ?? '').substring(0, 20) + '...' : (row?.services?.[0]?.service_name ?? '')}`}
                        </Typography>
                    )}{' '}
                </div>
            ),
        },
        {
            field: 'updated_at',
            headerName: 'Last Active',
            flex: 1,
            renderCell: ({row: {updated_at}}) => format(new Date(updated_at), 'MM/dd/yyyy'),
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: ({value}) => {
                if (value === 'active') {
                    return <Chip label="Active" color="success" variant="filled" />
                } else if (value === 'deactivated') {
                    return <Chip label="Deactivated" color="error" variant="filled" />
                } else if (value === 'removed') {
                    return <Chip label="Removed" color="error" variant="filled" />
                } else if (value === 'pending') {
                    return (
                        <Chip
                            label="Pending Invitation"
                            variant="filled"
                            sx={theme => ({
                                backgroundColor: theme.palette.warning.light,
                                color: theme.palette.warning.contrastText,
                            })}
                        />
                    )
                }
                return null
            },
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 0.4,
            sortable: false,
            renderCell: ({row}) => (
                <>
                    <IconButton
                        onClick={e => {
                            e.stopPropagation()
                            menuOpen(e, row)
                        }}>
                        <MoreVert />
                    </IconButton>
                </>
            ),
        },
    ]

    return (
        <>
            {mobile ? (
                <>
                    <div className="header">
                        <div className="title">
                            <IconButton onClick={() => setMobileSidebar(true)}>
                                <MenuIcon />
                            </IconButton>
                            <Typography variant="h6" color="text.primary">
                                Caregivers Management
                            </Typography>
                        </div>
                        <NotificationIcon />
                    </div>
                    <br />
                    <br />
                    <br />
                    <div className="actions">
                        <Button
                            variant="contained"
                            startIcon={<PersonAddAlt1 />}
                            onClick={() => navigate('/caregivers-management/caregiver-form')}
                            style={{flex: 1}}>
                            Add New Client
                        </Button>
                    </div>
                    <br />
                </>
            ) : (
                <>
                    <div className="header">
                        <Typography variant="h5" color="text.primary">
                            Caregivers Management
                        </Typography>
                        <div className="actions">
                            <Button
                                variant="contained"
                                startIcon={<PersonAddAlt1 />}
                                onClick={() => navigate('/caregivers-management/caregiver-form')}
                                style={{minWidth: 160}}>
                                Add New Caregiver
                            </Button>
                        </div>
                    </div>
                    <br />
                    <br />
                </>
            )}
            <div className="filter">
                <TextField
                    placeholder="Search caregiver ..."
                    style={{flex: 3}}
                    onChange={e => setSearchTyping(e.target.value)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
                {!mobile && (
                    <>
                        <FormControl style={{flex: 1}}>
                            <Select
                                multiple
                                value={selectedStatuses}
                                onChange={statusChange}
                                renderValue={renderStatusLabel}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            maxHeight: 300,
                                        },
                                    },
                                }}>
                                <MenuItem value="all">
                                    <Checkbox
                                        checked={selectedStatuses.length === statuses.length}
                                        indeterminate={selectedStatuses.length > 0 && selectedStatuses.length < statuses.length}
                                    />
                                    <ListItemText primary="Select All" />
                                </MenuItem>
                                {statuses.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <Checkbox checked={selectedStatuses.includes(item.id)} />
                                        <ListItemText primary={item.display_name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl style={{flex: 1}}>
                            <Select
                                multiple
                                value={selectedServices}
                                onChange={serviceChange}
                                renderValue={renderServiceLabel}
                                MenuProps={{
                                    PaperProps: {
                                        style: {maxHeight: 300},
                                        ref: menuRef,
                                    },
                                }}
                                onOpen={() => {
                                    setTimeout(() => {
                                        if (menuRef?.current) {
                                            menuRef.current.scrollTop = 0
                                        }
                                    }, 0)
                                }}>
                                <MenuItem disableRipple disableTouchRipple>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        placeholder="Search service name..."
                                        value={searchServiceTerm}
                                        onChange={e => setSearchServiceTerm(e.target.value)}
                                        onKeyDown={e => e.stopPropagation()}
                                        autoFocus
                                    />
                                </MenuItem>
                                <MenuItem value="all">
                                    <Checkbox
                                        checked={filteredServices.length > 0 && filteredServices.every(c => selectedServices.includes(c.id))}
                                        indeterminate={
                                            filteredServices.some(c => selectedServices.includes(c.id)) &&
                                            !filteredServices.every(c => selectedServices.includes(c.id))
                                        }
                                    />
                                    <ListItemText primary="Select All" />
                                </MenuItem>
                                {filteredServices.map(item => (
                                    <MenuItem key={item.id} value={item.id}>
                                        <Checkbox checked={selectedServices.includes(item.id)} />
                                        <div className="service-display">
                                            <Chip
                                                label={item?.billing_unit?.replace('_', ' ')?.toUpperCase()}
                                                color="action.selected"
                                                variant="filled"
                                                size="small"
                                                style={{fontWeight: 'bold', backgroundColor: '#0000000A'}}
                                            />
                                            <Typography variant="inputText">{item?.display_name}</Typography>
                                        </div>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </>
                )}
            </div>
            <br />
            {mobile ? (
                <>
                    <div className="mobile-filter" onClick={() => setOpenMobileFilter(true)}>
                        <FilterAlt color="primary" fontSize="medium" />
                        <Typography variant="buttonMedium" color="primary">
                            Filter
                        </Typography>
                    </div>
                    {skeleton && (
                        <SkeletonContainer>
                            {Array.from({length: 6}).map((_, index) => (
                                <div className="card" key={index}>
                                    <div className="top-card">
                                        <div className="left-card">
                                            <AvatarSkeleton size={32} />
                                            <div className="profile-card">
                                                <TextSkeleton width={index % 2 ? 84 : 100} />
                                                <TextSkeleton width={index % 2 ? 96 : 100} />
                                            </div>
                                        </div>
                                        <div className="right-card">
                                            <ChipSkeleton width={index % 3 == 2 ? 132 : 64} height={32} />
                                            <Skeleton variant="circular" width={24} height={24} sx={{ml: 1, mr: 0.5}} />
                                        </div>
                                    </div>
                                    <div className="form">
                                        <Typography variant="h9" color="text.primary">
                                            Services:
                                        </Typography>
                                        <TextSkeleton width={index % 2 ? 110 : 160} />
                                    </div>
                                    <div className="form">
                                        <Typography variant="h9" color="text.primary">
                                            Last Active:
                                        </Typography>
                                        <TextSkeleton width={80} />
                                    </div>
                                </div>
                            ))}
                        </SkeletonContainer>
                    )}
                    {scrollData?.length > 0 && (
                        <InfiniteScroll
                            dataLength={scrollData?.length}
                            next={loadScrollData}
                            hasMore={hasMoreScroll}
                            loader={
                                <Box display="flex" justifyContent="center" my={2}>
                                    <CircularProgress />
                                </Box>
                            }
                            endMessage={
                                <Typography align="center" sx={{mt: 2}}>
                                    All Loaded.
                                </Typography>
                            }>
                            {scrollData.map((row, index) => (
                                <div className="card" key={index} onClick={() => onRowClick(row)}>
                                    <div className="top-card">
                                        <div className="left-card">
                                            {row?.avatar_url ? (
                                                <Avatar src={row?.avatar_url} style={{width: 32, height: 32}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(row?.first_name + ' ' + row?.last_name),
                                                        width: 32,
                                                        height: 32,
                                                        fontSize: 16,
                                                    }}>
                                                    {getInitials(row?.first_name + ' ' + row?.last_name)}
                                                </Avatar>
                                            )}
                                            <div className="profile-card">
                                                <Typography variant="body2" color="text.primary" className="name">
                                                    {row?.first_name + ' ' + row?.last_name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" className="name">
                                                    {row?.email}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div className="right-card">
                                            {row?.status == 'active' && <Chip label="Active" color="success" variant="filled" />}
                                            {row?.status == 'deactivated' && <Chip label="Deactivated" color="error" variant="filled" />}
                                            {row?.status == 'pending' && (
                                                <Chip
                                                    label="Pending Invitation"
                                                    sx={{
                                                        bgcolor: theme => theme.palette.warning.light,
                                                        color: '#ffffff',
                                                    }}
                                                    variant="filled"
                                                />
                                            )}
                                            <IconButton
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    menuOpen(e, row)
                                                }}
                                                style={{marginLeft: -8, marginRight: -6}}>
                                                <MoreVert />
                                            </IconButton>
                                        </div>
                                    </div>
                                    <div className="form">
                                        <Typography variant="h9" color="text.primary">
                                            Services:
                                        </Typography>
                                        {row?.services && row?.services?.length > 1 ? (
                                            <Typography variant="body2" color="text.primary">
                                                {`${(row?.services?.[0]?.service_name ?? '').length > 20 ? (row?.services?.[0]?.service_name ?? '').substring(0, 20) + '...' : (row?.services?.[0]?.service_name ?? '')}, `}{' '}
                                                <b>+{row?.services?.length - 1}</b>
                                            </Typography>
                                        ) : (
                                            <Typography variant="body2" color="text.primary">
                                                {`${(row?.services?.[0]?.service_name ?? '').length > 20 ? (row?.services?.[0]?.service_name ?? '').substring(0, 20) + '...' : (row?.services?.[0]?.service_name ?? '')}`}
                                            </Typography>
                                        )}{' '}
                                    </div>
                                    <div className="form">
                                        <Typography variant="h9" color="text.primary">
                                            Last Active:
                                        </Typography>
                                        <Typography variant="body2" color="text.primary">
                                            {format(new Date(row?.updated_at), 'MM/dd/yyyy')}
                                        </Typography>
                                    </div>
                                </div>
                            ))}
                        </InfiniteScroll>
                    )}
                </>
            ) : (
                <>
                    {skeleton && (
                        <TableSkeleton
                            rows={pageSize}
                            rowHeight={66}
                            columns={[
                                {flex: 1.6, type: 'avatar', headerWidth: 80},
                                {flex: 1, widths: ['85%', '70%', '78%', '62%']},
                                {flex: 1, widths: ['70%', '66%', '72%']},
                                {flex: 1.2, widths: ['75%', '55%', '82%', '60%']},
                                {flex: 1, widths: ['55%']},
                                {flex: 1, type: 'chip', chipWidth: 64},
                                {flex: 0.4, type: 'actions'},
                            ]}
                        />
                    )}
                    {rows?.length > 0 && (
                        <DataGrid
                            style={{display: skeleton ? 'none' : 'inline'}}
                            rows={rows}
                            columns={columns}
                            paginationModel={{page, pageSize}}
                            onSortModelChange={sortChange}
                            disableRowSelectionOnClick
                            disableColumnMenu
                            getRowHeight={() => 66}
                            onRowClick={e => onRowClick(e.row)}
                            slots={{
                                footer: () => (
                                    <DataGridFooter page={page} setPage={setPage} pageSize={pageSize} setPageSize={setPageSize} rowCount={rowCount} />
                                ),
                            }}
                        />
                    )}
                </>
            )}
            {!skeleton && ((!mobile && rows?.length == 0) || (mobile && scrollData?.length == 0)) && (
                <>
                    <br />
                    <Typography variant="subtitle2" color="text.secondary">
                        Search Results (0)
                    </Typography>
                    <br />
                    <br />
                    <div style={{textAlign: 'center'}}>
                        <img src={noResultsFound} width={mobile ? '200' : '372'} />
                        <br />
                        <br />
                        <div style={{marginBottom: 10}}>
                            <Typography variant={mobile ? 'h7' : 'h5'} color="text.primary">
                                No Caregivers Match Your Search
                            </Typography>
                        </div>
                        <Typography variant={mobile ? 'body2' : 'body1'} color="text.primary">
                            Try adjusting your search terms or filter settings to find the caregiver you’re looking for.
                        </Typography>
                    </div>
                </>
            )}
            <br />
            <br />
            <br />
            <br />
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={menuClose}
                anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
                transformOrigin={{vertical: 'top', horizontal: 'right'}}>
                <div>
                    {selectedRow?.status == 'active' && (
                        <>
                            <MenuItem onClick={editCaregiverDetails}>Edit Caregiver Details</MenuItem>
                            <MenuItem onClick={deactivateCaregiver} sx={theme => ({color: theme.palette.error.main})}>
                                Deactivate Caregiver
                            </MenuItem>
                            <MenuItem onClick={removeCaregiver} sx={theme => ({color: theme.palette.error.main})}>
                                Remove Caregiver
                            </MenuItem>
                        </>
                    )}
                    {selectedRow?.status == 'deactivated' && (
                        <>
                            <MenuItem onClick={editCaregiverDetails}>Edit Caregiver Details</MenuItem>
                            <MenuItem onClick={reactivateCaregiver} sx={theme => ({color: theme.palette.error.main})}>
                                Re-activate Caregiver
                            </MenuItem>
                            <MenuItem onClick={removeCaregiver} sx={theme => ({color: theme.palette.error.main})}>
                                Remove Caregiver
                            </MenuItem>
                        </>
                    )}
                    {selectedRow?.status == 'pending' && (
                        <>
                            <MenuItem onClick={editCaregiverDetails}>Edit Caregiver Details</MenuItem>
                            <MenuItem onClick={removeCaregiver} sx={theme => ({color: theme.palette.error.main})}>
                                Remove Caregiver
                            </MenuItem>
                        </>
                    )}
                </div>
            </Menu>
            <MobileFilter
                open={openMobileFilter}
                setOpen={setOpenMobileFilter}
                statuses={statuses}
                selectedStatuses={selectedStatuses}
                setSelectedStatuses={setSelectedStatuses}
                setSortBy={setSortBy}
                setSortOrder={setSortOrder}
                mobileReset={mobileReset}
            />
            <DeactivateClientDialog
                open={openDeactivateClientDialog}
                setOpen={setOpenDeactivateClientDialog}
                data={selectedRow}
                loadData={() => loadData(true)}
            />
            <ReactivateClientDialog
                open={openReactivateClientDialog}
                setOpen={setOpenReactivateClientDialog}
                data={selectedRow}
                loadData={() => loadData(true)}
            />
        </>
    )
}
